import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type {
  DataISO,
  Dipendente,
  Evento,
  GiornoCalendario,
} from '../api/tipi.ts';
import {
  assenzaDi,
  contenutoCella,
  costruisciRighe,
  eventiDi,
  indicizzaEventi,
} from './griglia.ts';
import { corrisponde } from './controlli.ts';
import stili from './GrigliaCalendario.module.css';

/**
 * Il calendario classico: righe = dipendenti (a blocchi di 3 o 4), colonne = giorni.
 *
 * E' la vista di lavoro principale su desktop, quella su cui il personale ha
 * gia' anni di abitudine. Serve anche alla vista globale per sede, con
 * `raggruppaPerSede` attivo.
 *
 * Il vincolo che determina l'intera implementazione: 184 dipendenti per 3-4
 * righe per 365 giorni fanno circa 200.000 celle. Renderizzarle tutte blocca
 * il browser per secondi e consuma centinaia di MB, quindi si virtualizza su
 * ENTRAMBI gli assi e in ogni istante esistono nel DOM poche centinaia di
 * nodi. E' anche il motivo per cui le celle sono posizionate in modo assoluto
 * invece di stare in una `<table>`: una tabella HTML impone al browser di
 * calcolare la larghezza delle colonne su tutte le righe.
 */

const ALTEZZA_RIGA = 26;
const LARGHEZZA_COLONNA = 116;
const LARGHEZZA_ETICHETTA = 220;
const ALTEZZA_INTESTAZIONE = 44;

// Contesto lasciato prima della riga/colonna di apertura: senza, la
// posizione iniziale calcolata qui sotto la metterebbe esattamente al bordo
// superiore/sinistro del contenitore, tagliata a meta'.
const MARGINE_PRIMA_RIGA = 3 * ALTEZZA_RIGA;
const MARGINE_PRIMA_COLONNA = 2 * LARGHEZZA_COLONNA;

const GIORNI_SETTIMANA = ['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom'];

export interface CellaSelezionata {
  dipendenteId: string;
  data: DataISO;
}

export interface ProprietaGriglia {
  giorni: readonly GiornoCalendario[];
  dipendenti: readonly Dipendente[];
  eventi: readonly Evento[];
  /** Raggruppa per sede e reparto: e' la vista globale. */
  raggruppaPerSede?: boolean;
  /** Ids dei dipendenti su cui l'utente corrente puo' scrivere. */
  dipendentiModificabili?: ReadonlySet<string>;
  /** Chi sta guardando: all'apertura il calendario si posiziona sulla sua riga. */
  dipendenteProprio?: string | null;
  /** Testo cercato: evidenzia le righe corrispondenti senza nascondere le altre. */
  ricerca?: string;
  /** Dipendente su cui posizionarsi, quando la ricerca trova qualcuno. */
  dipendenteDaMostrare?: string | null;
  /**
   * Contatore delle richieste di tornare a oggi.
   *
   * Il pulsante "Oggi" sta nella barra dei controlli, che non puo' scorrere
   * la griglia perche' non e' lei a possederla: cambia numero a ogni clic, e
   * la griglia centra la colonna di oggi. Il valore non significa niente.
   */
  centraSuOggi?: number;
  onApriCella?: (cella: CellaSelezionata, eventi: readonly Evento[]) => void;
}

export function GrigliaCalendario({
  giorni,
  dipendenti,
  eventi,
  raggruppaPerSede = false,
  dipendentiModificabili,
  dipendenteProprio = null,
  ricerca = '',
  dipendenteDaMostrare = null,
  centraSuOggi = 0,
  onApriCella,
}: ProprietaGriglia) {
  const contenitore = useRef<HTMLDivElement>(null);
  const [selezione, setSelezione] = useState<CellaSelezionata | null>(null);

  const righe = useMemo(
    () => costruisciRighe(dipendenti, { raggruppaPerSede }),
    [dipendenti, raggruppaPerSede],
  );

  // Espansione degli intervalli in celle: una volta sola, non a ogni disegno.
  const indiceEventi = useMemo(() => indicizzaEventi(eventi), [eventi]);

  // Calcolati qui, PRIMA dei virtualizzatori, cosi' la loro posizione di
  // apertura (vedi `initialOffset` sotto) puo' usarli fin dal primo render.
  const indiceRigaPropria = useMemo(() => {
    if (!dipendenteProprio) return -1;
    return righe.findIndex((r) => r.dipendente?.id === dipendenteProprio && r.primaDelBlocco);
  }, [righe, dipendenteProprio]);

  const indiceOggi = useMemo(() => {
    const oggi = dataDiOggi();
    return giorni.findIndex((g) => g.data === oggi);
  }, [giorni]);

  const virtualizzatoreRighe = useVirtualizer({
    count: righe.length,
    getScrollElement: () => contenitore.current,
    estimateSize: () => ALTEZZA_RIGA,
    // `paddingStart` riserva lo spazio dell'intestazione dentro il calcolo del
    // virtualizzatore. Sottrarlo a mano dagli offset funzionerebbe finche' non
    // si usa `scrollToIndex`, che poi manderebbe la riga sotto l'intestazione.
    paddingStart: ALTEZZA_INTESTAZIONE,
    overscan: 10,
    // Posizione di apertura sulla riga di chi guarda: si veda il commento su
    // `offsetIniziale` per il perche' di questo valore approssimato invece di
    // un centraggio esatto.
    initialOffset: offsetIniziale(indiceRigaPropria, ALTEZZA_RIGA, ALTEZZA_INTESTAZIONE, MARGINE_PRIMA_RIGA),
  });

  const virtualizzatoreColonne = useVirtualizer({
    horizontal: true,
    count: giorni.length,
    getScrollElement: () => contenitore.current,
    estimateSize: () => LARGHEZZA_COLONNA,
    paddingStart: LARGHEZZA_ETICHETTA,
    overscan: 4,
    // Posizione di apertura sulla colonna di oggi, stesso ragionamento.
    initialOffset: offsetIniziale(indiceOggi, LARGHEZZA_COLONNA, LARGHEZZA_ETICHETTA, MARGINE_PRIMA_COLONNA),
  });

  const righeVisibili = virtualizzatoreRighe.getVirtualItems();
  const colonneVisibili = virtualizzatoreColonne.getVirtualItems();

  /**
   * Rifinisce la posizione di apertura calcolata sopra con `initialOffset`:
   * quella e' una stima a margine fisso, utile subito perche' non richiede
   * di misurare il contenitore; questa la centra con esattezza, non appena
   * il contenitore e' misurabile per davvero.
   *
   * Scatta una volta sola, con un ref invece che con uno stato: se scattasse
   * a ogni ricaricamento dei dati (es. dopo un inserimento, che rifà la
   * `carica()` in App.tsx e produce un nuovo array `righe`) riporterebbe
   * l'utente alla propria riga anche se nel frattempo si era spostato
   * altrove in griglia — esattamente il comportamento che "Vai a Oggi" evita
   * di sua natura per lo scorrimento verticale.
   *
   * Lo scorrimento e' rimandato con un `setTimeout(..., 0)` invece di essere
   * chiamato subito. Il motivo non e' l'altezza del contenitore — quella e'
   * gia' misurata al momento in cui questo effetto gira — ma la Strict Mode
   * di React: in sviluppo, dopo aver montato gli effetti una prima volta,
   * React li rimonta una seconda volta per scovare effetti collaterali non
   * idempotenti, e in quel secondo giro il virtualizzatore riporta da solo
   * lo scorrimento alla propria posizione iniziale DOPO che questo effetto ha
   * gia' girato una volta. Rimandando l'azione al giro successivo
   * dell'event loop, la si esegue dopo che quella pantomima e' gia' finita,
   * e resta l'ultima parola.
   */
  const posizionatoAllApertura = useRef(false);
  useEffect(() => {
    if (posizionatoAllApertura.current) return;
    if (righe.length === 0) return;

    const idTimer = window.setTimeout(() => {
      if (indiceOggi >= 0) {
        virtualizzatoreColonne.scrollToIndex(indiceOggi, { align: 'center' });
      }
      if (dipendenteProprio) {
        const indiceRiga = righe.findIndex(
          (r) => r.dipendente?.id === dipendenteProprio && r.primaDelBlocco,
        );
        if (indiceRiga >= 0) {
          virtualizzatoreRighe.scrollToIndex(indiceRiga, { align: 'center' });
        }
      }
      posizionatoAllApertura.current = true;
    }, 0);

    return () => window.clearTimeout(idTimer);
  }, [righe, indiceOggi, dipendenteProprio, virtualizzatoreColonne, virtualizzatoreRighe]);

  /**
   * Tornare a oggi centra la colonna del giorno ma NON tocca lo scorrimento
   * verticale se una cella e' selezionata: la specifica chiede esplicitamente
   * di non far perdere all'utente la riga su cui stava lavorando. Solo quando
   * la riga selezionata e' finita fuori vista la si riporta dentro.
   */
  const vaiAOggi = useCallback(() => {
    if (indiceOggi >= 0) {
      virtualizzatoreColonne.scrollToIndex(indiceOggi, { align: 'center' });
    }
    if (!selezione) return;

    const indiceRiga = righe.findIndex(
      (r) => r.dipendente?.id === selezione.dipendenteId && r.primaDelBlocco,
    );
    if (indiceRiga < 0) return;

    const giaVisibile = righeVisibili.some(
      (r) => r.index >= indiceRiga && r.index <= indiceRiga + 3,
    );
    if (!giaVisibile) {
      virtualizzatoreRighe.scrollToIndex(indiceRiga, { align: 'center' });
    }
  }, [indiceOggi, righe, righeVisibili, selezione, virtualizzatoreColonne, virtualizzatoreRighe]);

  /*
   * Il pulsante "Oggi" della barra arriva qui come contatore.
   *
   * Prima esistevano due pulsanti: "Oggi" nella barra, che spostava la
   * finestra, e "Vai a Oggi" dentro la griglia, che ne centrava la colonna.
   * Il secondo non poteva funzionare quando oggi cadeva fuori dalla finestra
   * - un'altra settimana, un altro mese, l'anno prossimo - perche' quella
   * colonna semplicemente non c'era, e chi lo premeva vedeva non succedere
   * niente. Ora il gesto e' uno solo e le due meta' avvengono insieme.
   *
   * La chiamata passa da un ref perche' l'effetto deve dipendere dal
   * contatore e da nient'altro: mettendo `vaiAOggi` fra le dipendenze la
   * griglia tornerebbe a oggi da sola ogni volta che cambia una selezione.
   */
  const ultimoVaiAOggi = useRef(vaiAOggi);
  useEffect(() => {
    ultimoVaiAOggi.current = vaiAOggi;
  }, [vaiAOggi]);

  useEffect(() => {
    if (centraSuOggi === 0) return;
    ultimoVaiAOggi.current();
  }, [centraSuOggi]);

  // Quando la ricerca trova qualcuno, la sua riga viene portata in vista.
  // E' il motivo per cui la ricerca non filtra: si vuole vedere quella persona
  // SENZA perdere di vista chi altro e' assente negli stessi giorni.
  useEffect(() => {
    if (!dipendenteDaMostrare) return;
    const indice = righe.findIndex(
      (r) => r.dipendente?.id === dipendenteDaMostrare && r.primaDelBlocco,
    );
    if (indice >= 0) virtualizzatoreRighe.scrollToIndex(indice, { align: 'center' });
  }, [dipendenteDaMostrare, righe, virtualizzatoreRighe]);

  const larghezzaTotale = virtualizzatoreColonne.getTotalSize();

  return (
    <div className={stili.riquadro}>
      <div className={stili.barra}>
        <Legenda />
      </div>

      <div ref={contenitore} className={stili.contenitore}>
        <div
          style={{
            height: virtualizzatoreRighe.getTotalSize(),
            width: larghezzaTotale,
            position: 'relative',
          }}
        >
          <div
            className={stili.intestazione}
            style={{ height: ALTEZZA_INTESTAZIONE, width: larghezzaTotale }}
          >
            <div
              className={stili.angolo}
              style={{ width: LARGHEZZA_ETICHETTA, height: ALTEZZA_INTESTAZIONE }}
            >
              Sede / Dipendente
            </div>
            {colonneVisibili.map((colonna) => {
              const giorno = giorni[colonna.index];
              if (!giorno) return null;
              return (
                <div
                  key={colonna.key}
                  className={`${stili.cellaIntestazione} ${classeGiorno(giorno, stili)}`}
                  style={{ left: colonna.start, width: colonna.size }}
                  title={giorno.festivita ?? undefined}
                >
                  <span className={stili.giornoSettimana}>
                    {GIORNI_SETTIMANA[giornoDellaSettimana(giorno.data)]}
                  </span>
                  <span className={stili.giornoNumero}>{formattaGiorno(giorno.data)}</span>
                </div>
              );
            })}
          </div>

          {righeVisibili.map((rigaVirtuale) => {
            const riga = righe[rigaVirtuale.index];
            if (!riga) return null;

            if (riga.tipo === 'intestazioneGruppo') {
              return (
                <div
                  key={riga.chiave}
                  className={stili.rigaGruppo}
                  style={{
                    top: rigaVirtuale.start,
                    height: rigaVirtuale.size,
                    width: larghezzaTotale,
                  }}
                >
                  <div className={stili.etichettaGruppo} style={{ width: LARGHEZZA_ETICHETTA }}>
                    {riga.gruppo}
                  </div>
                </div>
              );
            }

            const dipendente = riga.dipendente!;
            const modificabile = dipendentiModificabili?.has(dipendente.id) ?? false;

            return (
              <div
                key={riga.chiave}
                className={[
                  stili.riga,
                  riga.ultimaDelBlocco ? stili.fineBlocco : '',
                  dipendente.stato === 'CESSATO' ? stili.cessato : '',
                  ricerca !== '' && corrisponde(dipendente, ricerca) ? stili.trovata : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{
                  top: rigaVirtuale.start,
                  height: rigaVirtuale.size,
                  width: larghezzaTotale,
                }}
              >
                <div className={stili.etichetta} style={{ width: LARGHEZZA_ETICHETTA }}>
                  {riga.primaDelBlocco && (
                    <>
                      <span className={stili.sede}>{dipendente.repartoNome}</span>
                      <span className={stili.nome}>{dipendente.nomeCompleto}</span>
                    </>
                  )}
                </div>

                {colonneVisibili.map((colonna) => {
                  const giorno = giorni[colonna.index];
                  if (!giorno) return null;

                  const eventiCella = eventiDi(indiceEventi, dipendente.id, giorno.data);
                  const assenza = assenzaDi(indiceEventi, dipendente.id, giorno.data);
                  const testo = contenutoCella(riga, eventiCella);
                  const selezionata =
                    selezione?.dipendenteId === dipendente.id &&
                    selezione.data === giorno.data;

                  return (
                    <div
                      key={colonna.key}
                      className={[
                        stili.cella,
                        classeGiorno(giorno, stili),
                        // Il verde e' lo stato approvato, non il tipo evento:
                        // e' esattamente il significato che aveva nel file.
                        // Lo decide l'assenza: una trasferta accanto non e'
                        // qualcosa che si approva, e non deve tingere la cella.
                        assenza?.stato === 'APPROVATO' ? stili.approvato : '',
                        selezionata ? stili.selezionata : '',
                        modificabile ? stili.modificabile : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={{ left: colonna.start, width: colonna.size }}
                      title={testo || undefined}
                      onClick={() => {
                        const cella = { dipendenteId: dipendente.id, data: giorno.data };
                        setSelezione(cella);
                        if (modificabile) onApriCella?.(cella, eventiCella);
                      }}
                    >
                      {testo}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Legenda() {
  return (
    <div className={stili.legenda}>
      <span>
        <i className={`${stili.pastiglia} ${stili.pastigliaApprovato}`} /> approvato
      </span>
      <span>
        <i className={`${stili.pastiglia} ${stili.pastigliaFestivita}`} /> festivita
      </span>
      <span>
        <i className={`${stili.pastiglia} ${stili.pastigliaWeekend}`} /> weekend
      </span>
    </div>
  );
}

/**
 * Festivita' e weekend hanno due tonalita' diverse, come nel file originale:
 * rosa intenso per le festivita' aziendali, rosa tenue per i sabati e le
 * domeniche. Appiattirle su un colore solo farebbe sparire un'informazione
 * che nella griglia attuale si legge a colpo d'occhio.
 */
function classeGiorno(
  giorno: GiornoCalendario,
  css: Record<string, string>,
): string {
  if (giorno.festivita) return css['festivo'] ?? '';
  if (giorno.weekend) return css['weekend'] ?? '';
  return '';
}

function giornoDellaSettimana(data: DataISO): number {
  // getUTCDay: 0 = domenica. Riportato a 0 = lunedi' per l'indice italiano.
  const giorno = new Date(`${data}T00:00:00Z`).getUTCDay();
  return (giorno + 6) % 7;
}

function formattaGiorno(data: DataISO): string {
  return `${data.slice(8, 10)}/${data.slice(5, 7)}`;
}

function dataDiOggi(): DataISO {
  // `sv-SE` produce `AAAA-MM-GG`, ed e' il modo piu' breve di ottenere la
  // data locale in formato ISO senza passare da toISOString(), che
  // convertirebbe in UTC e a fine giornata restituirebbe domani.
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' });
}

/**
 * Offset di apertura per un asse del virtualizzatore: mette l'indice cercato
 * a poca distanza dal bordo iniziale, invece che esattamente a ridosso.
 *
 * E' una posizione "abbastanza buona", non il centraggio esatto che fa
 * il centraggio su oggi: quello richiede di conoscere la dimensione reale del
 * contenitore, che al primo render non esiste ancora (il ref del DOM si
 * popola solo dopo il montaggio). Questa versione approssimata non ha
 * bisogno di misurare nulla, quindi puo' calcolarsi da subito e dare alla
 * calendario una posizione sensata fin dal primissimo fotogramma, corretta poi
 * dal centraggio preciso non appena il contenitore e' misurabile.
 */
function offsetIniziale(
  indice: number,
  dimensioneElemento: number,
  inizioContenuto: number,
  margine: number,
): number {
  if (indice < 0) return 0;
  return Math.max(0, inizioContenuto + indice * dimensioneElemento - margine);
}
